import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ToastrService } from "ngx-toastr";
import {
  DispatchTrip,
  DispatchTripStatus,
  DispatchTripStatusLabels,
  DispatcherUser,
} from "@app/shared/models/DispatchTrip.model";
import { DispatchTripService } from "../shared/dispatch-trip.service";
import { clientService } from "@app/client/shared/client.service";
import { DriverService } from "@app/driver/shared/driver.service";
import { ProductService } from "@app/product/shared/products.service";
import { TractorService } from "@app/tractor/shared/tractor.service";
import { TrailerService } from "@app/trailer/shared/trailer.service";
import { authenticationService } from "@app/login/auth.service";
import { forkJoin } from "rxjs";
import { FileUploadService } from "@app/shared/services/files/fileUpload.service";
import { NewMixedPreviewFileComponent } from "@app/shared/components/newmixed-preview-file/newmixed-preview-file.component";
import { FeatureFlagsService } from "@app/agilx/shared/feature-flags.service";
import { EmailComposeData } from "../email-compose-modal/email-compose-modal.component";

@Component({
  selector: "app-dispatch-trip-modal",
  templateUrl: "./dispatch-trip-modal.component.html",
  styleUrls: ["./dispatch-trip-modal.component.css"],
})
export class DispatchTripModalComponent implements OnInit {
  @Input() visible: boolean = false;
  @Input() tripId: string | null = null;
  @Input() initialData: Partial<DispatchTrip> = {};

  // Prefilled values (alternative to initialData for simpler use)
  @Input() prefilledDriverId: string | null = null;
  @Input() prefilledClientId: string | null = null;
  @Input() prefilledBillToId: string | null = null;
  @Input() prefilledTractorId: string | null = null;
  @Input() prefilledTrailerId: string | null = null;
  @Input() prefilledProductId: string | null = null;
  @Input() prefilledStartDate: Date | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<DispatchTrip>();
  @Output() cancelled = new EventEmitter<void>();

  tripForm: FormGroup;
  isLoading = false;
  isSaving = false;
  isEditMode = false;

  // Dropdown options
  clients: any[] = [];
  billToClients: any[] = [];
  drivers: any[] = [];
  products: any[] = [];
  tractors: any[] = [];
  trailers: any[] = [];
  dispatchers: DispatcherUser[] = [];

  statusOptions = [
    { label: "Requested", value: DispatchTripStatus.Requested },
    { label: "Pending", value: DispatchTripStatus.Pending },
    { label: "In Transit", value: DispatchTripStatus.InTransit },
    { label: "Delivered", value: DispatchTripStatus.Delivered },
  ];

  // Location picker data
  loadLocation: any = null;
  loadLocationLocId: string = "";
  unloadLocation: any = null;
  unloadLocationLocId: string = "";

  // File attachments
  @ViewChild("mixedPreview") mixedPreviewComponent!: NewMixedPreviewFileComponent;
  attachedFiles: any[] = [];

  // Email tracking
  clientEmailSentUtc: Date | null = null;
  driverEmailSentUtc: Date | null = null;
  sendingClientEmail = false;
  sendingDriverEmail = false;

  // Email compose modal
  showEmailCompose = false;
  emailComposeData: EmailComposeData | null = null;
  pendingEmailType: 'client' | 'driver' | null = null;

  get displayFiles(): any[] {
    return this.attachedFiles.filter((f: any) => !f.softDelete);
  }

  get emailFeaturesEnabled(): boolean {
    return this.featureFlagsService.isEnabled('emailFeatures');
  }

  constructor(
    private fb: FormBuilder,
    private dispatchTripService: DispatchTripService,
    private clientService: clientService,
    private driverService: DriverService,
    private productService: ProductService,
    private tractorService: TractorService,
    private trailerService: TrailerService,
    private authService: authenticationService,
    private toastr: ToastrService,
    private fileUploadService: FileUploadService,
    private featureFlagsService: FeatureFlagsService
  ) {
    this.tripForm = this.createForm();
  }

  ngOnInit(): void {
    this.loadDropdownData();
  }

  ngOnChanges(): void {
    if (this.visible) {
      this.isEditMode = !!this.tripId;
      if (this.tripId) {
        this.loadTrip();
      } else {
        this.resetForm();
        if (Object.keys(this.initialData).length > 0) {
          this.applyInitialData();
        } else if (this.hasPrefilledValues()) {
          this.applyPrefilledValues();
        }
      }
    }
  }

  private hasPrefilledValues(): boolean {
    return !!(
      this.prefilledDriverId ||
      this.prefilledClientId ||
      this.prefilledBillToId ||
      this.prefilledTractorId ||
      this.prefilledTrailerId ||
      this.prefilledProductId ||
      this.prefilledStartDate
    );
  }

  private applyPrefilledValues(): void {
    const patchData: any = {};

    if (this.prefilledDriverId) patchData.driverId = this.prefilledDriverId;
    if (this.prefilledClientId) patchData.clientId = this.prefilledClientId;
    if (this.prefilledBillToId) patchData.billToId = this.prefilledBillToId;
    if (this.prefilledTractorId) patchData.tractorId = this.prefilledTractorId;
    if (this.prefilledTrailerId) patchData.trailerId = this.prefilledTrailerId;
    if (this.prefilledProductId) patchData.productId = this.prefilledProductId;
    if (this.prefilledStartDate) patchData.startDate = new Date(this.prefilledStartDate);

    if (Object.keys(patchData).length > 0) {
      this.tripForm.patchValue(patchData);
    }
  }

  private createForm(): FormGroup {
    return this.fb.group({
      id: [null],
      referenceNumber: ["", Validators.required],
      startDate: [null],
      bolNumber: [""],
      clientId: [null],
      billToId: [null],
      loadLocationId: [null],
      unloadLocationId: [null],
      deliveryWindowStartDate: [null],
      deliveryWindowEndDate: [null],
      driverId: [null],
      productId: [null],
      status: [DispatchTripStatus.Requested],
      dispatcherId: [null],
      driverNotes: [""],
      clientNotes: [""],
      tractorId: [null],
      trailerId: [null],
    });
  }

  private loadDropdownData(): void {
    this.isLoading = true;

    forkJoin({
      clients: this.clientService.getClientList(),
      billTos: this.clientService.listAllBillTo(),
      drivers: this.driverService.getDriverList(false),
      products: this.productService.list(),
      tractors: this.tractorService.listSelectFormat(),
      trailers: this.trailerService.listSelectFormat(),
      dispatchers: this.dispatchTripService.getDispatchers(),
    }).subscribe({
      next: (data: any) => {
        this.clients = data.clients; 
        this.billToClients = data.billTos; 
        
        // Format drivers to show "Last, First" format
        this.drivers = data.drivers.map((driver: any) => ({
          ...driver,
          displayName: driver.lastName && driver.name 
            ? `${driver.lastName}, ${driver.name}` 
            : driver.name || driver.lastName || 'Unknown Driver'
        }));
        
        this.products = data.products;
        this.tractors = data.tractors;
        this.trailers = data.trailers;
        this.dispatchers = data.dispatchers;
        
        // Set current user as default dispatcher for new trips
        if (!this.isEditMode) {
          this.setDefaultDispatcher();
        }
        
        this.isLoading = false;
      },
      error: (error) => {
        this.toastr.error("Failed to load form data");
        console.error(error);
        this.isLoading = false;
      },
    });
  }

  private loadTrip(): void {
    if (!this.tripId) return;

    this.isLoading = true;
    this.dispatchTripService.getById(this.tripId).subscribe({
      next: (trip: any) => {
        this.tripForm.patchValue({
          id: trip.id,
          referenceNumber: trip.referenceNumber,
          startDate: trip.startDate ? new Date(trip.startDate) : null,
          bolNumber: trip.bolNumber,
          clientId: trip.clientId,
          billToId: trip.billToId,
          loadLocationId: trip.loadLocationId,
          unloadLocationId: trip.unloadLocationId,
          deliveryWindowStartDate: trip.deliveryWindowStartDate
            ? new Date(trip.deliveryWindowStartDate)
            : null,
          deliveryWindowEndDate: trip.deliveryWindowEndDate
            ? new Date(trip.deliveryWindowEndDate)
            : null,
          driverId: trip.driverId,
          productId: trip.productId,
          status: trip.status,
          dispatcherId: trip.dispatcherId,
          driverNotes: trip.driverNotes,
          clientNotes: trip.clientNotes,
          tractorId: trip.tractorId,
          trailerId: trip.trailerId,
        });
        
        // Set location picker data - properly handle null/undefined
        if (trip.loadLocation) {
          this.loadLocation = trip.loadLocation;
          this.loadLocationLocId = trip.loadLocation.locId || "";
        } else {
          this.loadLocation = null;
          this.loadLocationLocId = "";
        }
        
        if (trip.unloadLocation) {
          this.unloadLocation = trip.unloadLocation;
          this.unloadLocationLocId = trip.unloadLocation.locId || "";
        } else {
          this.unloadLocation = null;
          this.unloadLocationLocId = "";
        }

        // Load attached files - map contentType to type for the preview component
        this.attachedFiles = (trip.files || []).map((f: any) => ({
          ...f,
          type: f.contentType || f.type
        }));
        setTimeout(() => {
          if (this.mixedPreviewComponent) {
            this.mixedPreviewComponent.initComponent(this.attachedFiles);
          }
        });

        // Load email sent timestamps
        this.clientEmailSentUtc = trip.clientEmailSentUtc ? new Date(trip.clientEmailSentUtc) : null;
        this.driverEmailSentUtc = trip.driverEmailSentUtc ? new Date(trip.driverEmailSentUtc) : null;

        this.isLoading = false;
      },
      error: (error) => {
        this.toastr.error("Failed to load trip");
        console.error(error);
        this.isLoading = false;
      },
    });
  }

  private applyInitialData(): void {    
    const patchData: any = {};
    
    if (this.initialData.driverId) patchData.driverId = this.initialData.driverId;
    if (this.initialData.clientId) patchData.clientId = this.initialData.clientId;
    if (this.initialData.billToId) patchData.billToId = this.initialData.billToId;
    if (this.initialData.tractorId) patchData.tractorId = this.initialData.tractorId;
    if (this.initialData.trailerId) patchData.trailerId = this.initialData.trailerId;
    if (this.initialData.productId) patchData.productId = this.initialData.productId;
    if (this.initialData.startDate) patchData.startDate = new Date(this.initialData.startDate);

    if (Object.keys(patchData).length > 0) {
      this.tripForm.patchValue(patchData);
    }
  }

  private resetForm(): void {
    this.tripForm.reset({
      status: DispatchTripStatus.Requested,
    });
    
    // Reset location picker data
    this.loadLocation = null;
    this.loadLocationLocId = "";
    this.unloadLocation = null;
    this.unloadLocationLocId = "";

    // Reset files
    this.attachedFiles = [];
    if (this.mixedPreviewComponent) {
      this.mixedPreviewComponent.initComponent([]);
    }

    // Reset email state
    this.clientEmailSentUtc = null;
    this.driverEmailSentUtc = null;
  }

  private setDefaultDispatcher(): void {
    const currentUsername = this.authService.getUsername();
    if (!currentUsername || !this.dispatchers || this.dispatchers.length === 0) {
      return;
    }
    
    // Try to match by name or email
    const currentDispatcher = this.dispatchers.find(
      (d) => d.name === currentUsername || d.email === currentUsername
    );
    
    if (currentDispatcher) {
      this.tripForm.patchValue({ dispatcherId: currentDispatcher.id });
    }
  }

  onDriverChange(): void {
    const driverId = this.tripForm.get("driverId")?.value;
    if (driverId) {
      // Auto-fill tractor/trailer from driver if available
      const driver = this.drivers.find((d) => d.id === driverId);
      if (driver) {
        if (driver.tractorId && !this.tripForm.get("tractorId")?.value) {
          this.tripForm.patchValue({ tractorId: driver.tractorId });
        }
        if (driver.trailerId && !this.tripForm.get("trailerId")?.value) {
          this.tripForm.patchValue({ trailerId: driver.trailerId });
        }
      }
    }
  }

  onClientChange(): void {
    const clientId = this.tripForm.get("clientId")?.value;
    // Filter bill-to clients based on selected client
    if (clientId) {
      const filteredBillTos = this.billToClients.filter((bt: any) => bt.clientId === clientId);
      // If only one bill-to, auto-select it
      if (filteredBillTos.length === 1) {
        this.tripForm.patchValue({ billToId: filteredBillTos[0].id });
      }
    }
  }

  getFilteredBillToClients(): any[] {
    const clientId = this.tripForm.get("clientId")?.value;
    if (!clientId) return this.billToClients;
    return this.billToClients.filter((bt: any) => bt.clientId === clientId);
  }

  onLoadLocationSelected(location: any): void {
    if (location) {
      this.loadLocation = location;
      this.loadLocationLocId = location.locId;
      this.tripForm.patchValue({ loadLocationId: location.id });
    }
  }

  onUnloadLocationSelected(location: any): void {
    if (location) {
      this.unloadLocation = location;
      this.unloadLocationLocId = location.locId;
      this.tripForm.patchValue({ unloadLocationId: location.id });
    }
  }

  save(): void {
    if (this.tripForm.invalid) {
      this.tripForm.markAllAsTouched();
      this.toastr.error("Please fill in all required fields");
      return;
    }

    const formValue = this.tripForm.value;
    
    // Validate against time off events before saving
    if (formValue.driverId && formValue.startDate) {
      this.validateAgainstTimeOff(formValue.driverId, formValue.startDate, () => {
        this.performSave(formValue);
      });
    } else {
      this.performSave(formValue);
    }
  }

  private validateAgainstTimeOff(driverId: string, startDate: Date, onSuccess: () => void): void {
    this.driverService.getDriverEvents(driverId).subscribe({
      next: (events: any) => {
        const tripDate = new Date(startDate);
        tripDate.setHours(0, 0, 0, 0);
        
        // Check if trip date overlaps with any time off event
        const overlappingEvent = events.find((event: any) => {
          const eventStart = new Date(event.start);
          const eventEnd = new Date(event.end);
          eventStart.setHours(0, 0, 0, 0);
          eventEnd.setHours(23, 59, 59, 999);
          
          return tripDate >= eventStart && tripDate <= eventEnd;
        });
        
        if (overlappingEvent) {
          const eventType = this.getEventTypeName(overlappingEvent.eventType);
          this.toastr.error(
            `Driver has ${eventType} scheduled on ${tripDate.toLocaleDateString()}`,
            "Schedule Conflict"
          );
        } else {
          onSuccess();
        }
      },
      error: (error) => {
        console.error("Failed to check driver events:", error);
        // Continue with save even if validation fails
        onSuccess();
      },
    });
  }

  private getEventTypeName(eventType: number): string {
    const eventTypes: { [key: number]: string } = {
      0: "Sick Leave",
      1: "Home Time",
      2: "PTO",
      3: "Time Off",
    };
    return eventTypes[eventType] || "Time Off";
  }

  private performSave(formValue: any): void {
    this.isSaving = true;

    const doUpdate = (tripId: string) => {
      // Upload new files first
      const newFiles = this.attachedFiles.filter((f: any) => !f.id);
      const remainingIds = this.attachedFiles
        .filter((f: any) => f.id && !f.softDelete)
        .map((f: any) => f.id);

      if (newFiles.length > 0) {
        this.fileUploadService.uploadFiles(newFiles, "dispatchTrip").subscribe({
          next: (uploaded: any[]) => {
            // Save file references
            const fileInfos = uploaded.map((u: any) => ({
              fileName: u.name,
              originalFileName: u.originalName || u.name,
              contentType: u.type || "application/octet-stream",
            }));

            const remainingIds = this.attachedFiles
              .filter((f: any) => f.id && !f.softDelete)
              .map((f: any) => f.id);

            this.dispatchTripService.saveFiles(tripId, remainingIds, fileInfos).subscribe({
              next: () => this.emitSaved(formValue, tripId),
              error: (err: any) => this.handleSaveError(err, "Failed to save file references"),
            });
          },
          error: (err: any) => this.handleSaveError(err, "Failed to upload files"),
        });
      } else {
        // No new files, just update file references (handle removals)
        const remainingIds = this.attachedFiles
          .filter((f: any) => f.id && !f.softDelete)
          .map((f: any) => f.id);

        this.dispatchTripService.saveFiles(tripId, remainingIds, []).subscribe({
          next: () => this.emitSaved(formValue, tripId),
          error: (err: any) => this.handleSaveError(err, "Failed to save file references"),
        });
      }
    };

    if (this.isEditMode) {
      this.dispatchTripService.update(formValue).subscribe({
        next: () => {
          doUpdate(formValue.id);
        },
        error: (error) => this.handleSaveError(error),
      });
    } else {
      this.dispatchTripService.create(formValue).subscribe({
        next: (result) => {
          doUpdate(result.id);
        },
        error: (error) => this.handleSaveError(error),
      });
    }
  }

  private handleSaveError(error: any, fallbackMessage?: string): void {
    this.isSaving = false;
    const message = this.extractErrorMessage(error);
    const displayMsg = fallbackMessage && !message ? fallbackMessage : message || fallbackMessage || "Failed to save trip";
    this.toastr.error(displayMsg);

    // Highlight reference number field for duplicate errors
    if (message && message.toLowerCase().includes("reference number")) {
      this.tripForm.get("referenceNumber")?.markAsTouched();
      this.tripForm.get("referenceNumber")?.setErrors({ duplicate: true });
    }

    console.error(error);
  }

  private extractErrorMessage(error: any): string | null {
    if (typeof error === "string") return error;
    if (error.error) {
      if (typeof error.error === "string") return error.error;
      if (error.error.message) return error.error.message;
      if (error.error.title) return error.error.title;
    }
    if (error.message) return error.message;
    return null;
  }

  private emitSaved(formValue: any, id: string): void {
    this.toastr.success(this.isEditMode ? "Trip updated successfully" : "Trip created successfully");
    this.saved.emit({ ...formValue, id });
    this.close();
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.cancelled.emit();
    this.isSaving = false;
    // Reset files for next open
    this.attachedFiles = [];
  }

  onFileSelected(event: any): void {
    const files: FileList = event.target.files;
    if (!files.length) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      (file as any).softDelete = false;
      const maxId = this.attachedFiles.reduce((max, p) => {
        const id = p.id ? p.id : p.tempId;
        return id > max ? id : max;
      }, 0);
      (file as any).tempId = maxId + 1;
      this.attachedFiles.push(file);
    }

    this.mixedPreviewComponent?.initComponent(this.displayFiles);
    event.target.value = "";
  }

  removeFile(file: any): void {
    if (file && file.id) {
      file.softDelete = true;
    } else {
      const idx = this.attachedFiles.indexOf(file);
      if (idx >= 0) this.attachedFiles.splice(idx, 1);
    }
    this.mixedPreviewComponent?.initComponent(this.displayFiles);
  }

  sendClientEmail(): void {
    if (!this.tripId || this.sendingClientEmail) return;
    this.sendingClientEmail = true;
    this.dispatchTripService.getEmailPreview(this.tripId, 'client').subscribe({
      next: (preview) => {
        this.sendingClientEmail = false;
        this.emailComposeData = { type: 'client', ...preview };
        this.pendingEmailType = 'client';
        this.showEmailCompose = true;
      },
      error: (err) => {
        this.sendingClientEmail = false;
        this.toastr.error(err?.error || 'Failed to load email preview');
      },
    });
  }

  sendDriverEmail(): void {
    if (!this.tripId || this.sendingDriverEmail) return;
    this.sendingDriverEmail = true;
    this.dispatchTripService.getEmailPreview(this.tripId, 'driver').subscribe({
      next: (preview) => {
        this.sendingDriverEmail = false;
        this.emailComposeData = { type: 'driver', ...preview };
        this.pendingEmailType = 'driver';
        this.showEmailCompose = true;
      },
      error: (err) => {
        this.sendingDriverEmail = false;
        this.toastr.error(err?.error || 'Failed to load email preview');
      },
    });
  }

  onEmailComposeConfirmed(data: EmailComposeData): void {
    if (!this.tripId) return;
    const overrides = { to: data.to, subject: data.subject, body: data.body };
    const isSendingClient = data.type === 'client';
    if (isSendingClient) this.sendingClientEmail = true;
    else this.sendingDriverEmail = true;

    const send$ = isSendingClient
      ? this.dispatchTripService.sendClientEmail(this.tripId, overrides)
      : this.dispatchTripService.sendDriverEmail(this.tripId, overrides);

    send$.subscribe({
      next: (res) => {
        if (isSendingClient) {
          this.clientEmailSentUtc = new Date(res.sentUtc);
          this.sendingClientEmail = false;
          this.toastr.success('Client email sent');
        } else {
          this.driverEmailSentUtc = new Date(res.sentUtc);
          this.sendingDriverEmail = false;
          this.toastr.success('Driver email sent');
        }
      },
      error: (err) => {
        if (isSendingClient) this.sendingClientEmail = false;
        else this.sendingDriverEmail = false;
        this.toastr.error(err?.error || 'Failed to send email');
      },
    });
  }
}
